import {
  Children,
  cloneElement,
  isValidElement,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import CopyToClipboard from 'react-copy-to-clipboard'

import MapWrapper from '@/ui/MapWrapper/MapWrapper'
import classNames from 'classnames'
import { useInView } from 'framer-motion'

import {
  LngLatBounds,
  MapOptions,
  PolygonOptions,
  PolylineOptions,
} from '@2gis/mapgl/types'

import styles from './RecordCard.module.scss'

import dislike from './images/dislike.png'
import fire from './images/fire.png'
import like from './images/like.png'
import love from './images/love.png'
import rofl from './images/rofl.png'

export type RecordCardProps = {
  className?: string
  tag?: string
  title: string
  description: ReactNode
  mobileDescription?: ReactNode
  address: string
  index: number

  map: MapOptions
  mapImage?: string
  viewportBounds?: LngLatBounds
  fitPolylineToView?: boolean
  setSelectedObjects?: string[]
  polygon?: PolygonOptions
  polygons?: PolygonOptions[]
  markerCords?: number[]
  markerColor?: string
  markers?: number[][]
  polylineWithoutMarksSE?: boolean
  polyline?: PolylineOptions
  href: string

  setCurrentStep?: React.Dispatch<React.SetStateAction<number>>
}

const reactions = [like, love, fire, rofl, dislike]

const shortWords = [
  'в',
  'во',
  'к',
  'ко',
  'с',
  'со',
  'у',
  'о',
  'об',
  'обо',
  'от',
  'до',
  'из',
  'изо',
  'на',
  'по',
  'за',
  'под',
  'над',
  'при',
  'для',
  'без',
  'через',
  'про',
  'не',
  'ни',
  'и',
  'а',
  'но',
  'или',
  'да',
]

const shortWordsPattern = [...shortWords]
  .sort((first, second) => second.length - first.length)
  .join('|')

const hangingWordsRegex = new RegExp(
  `(^|[\\s([{«„"])(${shortWordsPattern})[ \\t]+`,
  'giu',
)

/**
 * Заменяет обычный пробел после короткого слова
 * на неразрывный пробел.
 */
const preventHangingWordsInText = (text: string): string => {
  return text.replace(hangingWordsRegex, '$1$2\u00A0')
}

/**
 * Рекурсивно обрабатывает ReactNode.
 *
 * Работает со:
 * - строками;
 * - JSX-фрагментами;
 * - вложенными span, p, strong и другими элементами;
 * - массивами React-элементов.
 *
 * br и остальные элементы сохраняются.
 */
const preventHangingWords = (node: ReactNode): ReactNode => {
  if (typeof node === 'string') {
    return preventHangingWordsInText(node)
  }

  if (
    node === null ||
    node === undefined ||
    typeof node === 'boolean' ||
    typeof node === 'number'
  ) {
    return node
  }

  if (Array.isArray(node)) {
    return Children.map(node, child => preventHangingWords(child))
  }

  if (isValidElement(node)) {
    const element = node as ReactElement<{
      children?: ReactNode
    }>

    if (element.props.children === undefined) {
      return element
    }

    return cloneElement(
      element,
      undefined,
      preventHangingWords(element.props.children),
    )
  }

  return node
}

const RecordCard = ({
  setSelectedObjects,
  description,
  mobileDescription,
  polygon,
  polygons,
  polyline,
  markerCords,
  markerColor,
  polylineWithoutMarksSE,
  markers,
  address,
  tag,
  title,
  map,
  mapImage,
  viewportBounds,
  fitPolylineToView,
  href,
  index,
  setCurrentStep,
  className,
}: RecordCardProps) => {
  const section = useRef<HTMLElement | null>(null)

  const mapContainer = useRef<HTMLDivElement | null>(null)

  const isInView = useInView(section, {
    amount: 0.5,
  })

  const isInViewMap = useInView(mapContainer, {
    margin: '200px 0px 200px 0px',
  })

  const [render, setRender] = useState(false)

  const [currReact, setCurrReact] = useState<null | number>(null)

  const [copy, setCopy] = useState(false)

  useEffect(() => {
    if (isInView) {
      setCurrentStep?.(index)
    }
  }, [setCurrentStep, isInView, index])

  useEffect(() => {
    setRender(isInViewMap)
  }, [isInViewMap])

  const preparedAddress = preventHangingWordsInText(address)

  const preparedDescription = preventHangingWords(description)

  const preparedMobileDescription = preventHangingWords(
    mobileDescription ?? description,
  )

  return (
    <article
      className={classNames(styles.card, 'card', 'record-card', className)}
      ref={section}
      id={`section${index}`}
      data-record-index={index}
    >
      <div
        className={classNames(
          styles.card_map_container,
          'card_map_container',
          'record-card__map-container',
        )}
        ref={mapContainer}
      >
        {mapImage ? (
          <img
            className="record-card__map-image"
            src={mapImage}
            alt={`Карта рекорда «${title}»`}
          />
        ) : render ? (
          <div className="record-card__map">
            <MapWrapper
              map={map}
              viewportBounds={viewportBounds}
              fitPolylineToView={fitPolylineToView}
              polyline={polyline}
              polygon={polygon}
              polygons={polygons}
              polylineWithoutMarksSE={polylineWithoutMarksSE}
              markers={markers}
              markerCords={markerCords}
              markerColor={markerColor}
              setSelectedObjects={setSelectedObjects}
            />
          </div>
        ) : null}

        <a
          className={classNames(
            'text',
            'text_color_primary',
            't-caption',
            styles.card_map_button,
            'card_map_button',
            'record-card__map-button',
          )}
          rel="noopener noreferrer"
          href={href}
        >
          <svg
            className="record-card__map-button-icon"
            width="25"
            height="24"
            viewBox="0 0 25 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <mask
              id={`record-card-map-mask-${index}`}
              style={{
                maskType: 'luminance',
              }}
              maskUnits="userSpaceOnUse"
              x="0"
              y="0"
              width="25"
              height="24"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6.46511 0.01055L7.49172 0L18.1738 0.00410896C18.3358 0.00578229 18.4364 0.00781208 18.5372 0.01055C19.0667 0.02461 19.6009 0.055625 20.1247 0.14867C20.6563 0.242955 21.1508 0.397202 21.6335 0.640359C22.1079 0.878967 22.5425 1.19077 22.919 1.56336C23.2954 1.93513 23.6109 2.36437 23.8522 2.83373C24.0986 3.31136 24.2542 3.80016 24.3496 4.32576C24.4437 4.8435 24.4751 5.37158 24.4893 5.89511C24.496 6.13537 24.4989 6.37564 24.5002 6.61631L24.5016 7.04416L24.5019 16.5258C24.5019 16.8112 24.5019 17.0965 24.5002 17.3822C24.4989 17.6225 24.496 17.8628 24.4893 18.103C24.4751 18.6262 24.4437 19.1546 24.3496 19.672C24.2542 20.1976 24.0986 20.6868 23.8522 21.1644C23.6109 21.6334 23.2954 22.0626 22.919 22.4348C22.5425 22.807 22.1079 23.1192 21.6335 23.3578C21.1508 23.6009 20.6563 23.7552 20.1247 23.8495C19.6009 23.9425 19.0667 23.9735 18.5372 23.9876L16.9417 24L6.82819 23.994C6.66624 23.9924 6.56567 23.9903 6.46511 23.9876C5.93553 23.9735 5.40134 23.9425 4.87762 23.8495C4.34595 23.7552 3.8515 23.6009 3.36877 23.3578C2.89399 23.1192 2.45978 22.807 2.0833 22.4348C1.70683 22.0626 1.39142 21.6334 1.14964 21.1644C0.90367 20.6868 0.748058 20.1976 0.652265 19.672C0.558145 19.1546 0.52719 18.6262 0.512968 18.103C0.506275 17.8628 0.503346 17.6225 0.502092 17.3822L0.500261 16.9539L0.5 7.47232C0.5 7.18699 0.5 6.90124 0.502092 6.61631C0.503346 6.37564 0.506275 6.13537 0.512968 5.89511C0.52719 5.37158 0.558145 4.8435 0.652265 4.32576C0.748058 3.80016 0.90367 3.31136 1.14964 2.83373C1.39142 2.36437 1.70683 1.93513 2.0833 1.56336C2.45978 1.19077 2.89399 0.878967 3.36877 0.640359C3.8515 0.397202 4.34595 0.242955 4.87762 0.14867C5.40134 0.055625 5.93553 0.02461 6.46511 0.01055Z"
                fill="white"
              />
            </mask>

            <g mask={`url(#record-card-map-mask-${index})`}>
              <rect
                x="0.5"
                y="-0.00195312"
                width="24.0019"
                height="24.0019"
                fill="#19AA1E"
              />

              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.5 -0.00195312H24.5019V7.87366L0.5 4.12337V-0.00195312Z"
                fill="#FFB919"
              />

              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.5 21.3743L24.5019 17.624V23.9995H0.5V21.3743Z"
                fill="#82D714"
              />

              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.5 3.64941L8.87804 4.95846C9.92096 4.29831 11.1617 3.93636 12.5009 3.93636C14.3558 3.93636 16.0218 4.63071 17.2349 5.85004C17.388 6.0039 17.5324 6.16478 17.6677 6.33187L24.5019 7.39968V8.34862L18.3617 7.38924C18.8179 8.26463 19.0639 9.23998 19.0639 10.2354C19.0639 11.5213 18.7763 12.8034 18.1818 14.0906L18.1636 14.1296L17.9111 14.6707H17.314C16.081 14.6707 15.2631 15.0395 14.7284 15.6932C14.3085 16.2064 14.0657 16.8983 13.9841 17.6532L13.9816 17.6768L13.9696 17.7974L13.9645 17.8498L13.9549 17.9525C13.9236 18.2895 13.9027 18.5753 13.8921 18.8082L24.5019 17.1504V18.0994L0.5 21.8497V20.9007L11.1218 19.2411L11.1171 19.028L11.1157 18.9679L11.1148 18.9372L11.1132 18.8896C11.1036 18.6313 11.0799 18.3001 11.042 17.8988L11.0347 17.8227L11.0217 17.6909C10.9455 16.9321 10.7063 16.2314 10.2876 15.7106C9.75844 15.0525 8.9481 14.6782 7.72502 14.6708L7.68783 14.6707H7.09073L6.83822 14.1296C6.23139 12.8293 5.93792 11.5343 5.93792 10.2354C5.93792 8.60191 6.6006 7.02239 7.76697 5.85004C7.80063 5.81619 7.83465 5.78276 7.86901 5.74973L0.5 4.59833V3.64941Z"
                fill="white"
              />

              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.5009 4.87305C15.8518 4.87305 18.1264 7.45222 18.1264 10.2345C18.1264 11.3514 17.8827 12.5138 17.314 13.7323C14.0167 13.7323 13.2029 16.0936 13.049 17.5793L13.0391 17.6783C12.987 18.2085 12.9568 18.634 12.9486 18.9548L12.0555 19.0943L12.0553 19.0654C12.0519 18.7292 12.0201 18.2576 11.96 17.6505L11.9573 17.624C11.8152 16.1415 11.0184 13.7323 7.68783 13.7323C7.11919 12.5138 6.87549 11.3514 6.87549 10.2345C6.87549 7.45222 9.15003 4.87305 12.5009 4.87305Z"
                fill="#0073FA"
              />
            </g>
          </svg>

          <span className="record-card__map-button-text">
            Посмотреть на карте
          </span>
        </a>
      </div>

      {tag && <div className="record_tag record-card__tag">{tag}</div>}

      <h2
        className={classNames(
          'text',
          't-header-h4',
          styles.title,
          'title',
          'record-card__title',
        )}
      >
        {title}
      </h2>

      <div
        className={classNames(
          styles.address_wrapper,
          'address_wrapper',
          'record-card__address-wrapper',
        )}
      >
        <svg
          className="record-card__address-icon"
          width="14"
          height="20"
          viewBox="0 0 14 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M7 9.5C6.33696 9.5 5.70107 9.23661 5.23223 8.76777C4.76339 8.29893 4.5 7.66304 4.5 7C4.5 6.33696 4.76339 5.70107 5.23223 5.23223C5.70107 4.76339 6.33696 4.5 7 4.5C7.66304 4.5 8.29893 4.76339 8.76777 5.23223C9.23661 5.70107 9.5 6.33696 9.5 7C9.5 7.3283 9.43534 7.65339 9.3097 7.95671C9.18406 8.26002 8.99991 8.53562 8.76777 8.76777C8.53562 8.99991 8.26002 9.18406 7.95671 9.3097C7.65339 9.43534 7.3283 9.5 7 9.5ZM7 0C5.14348 0 3.36301 0.737498 2.05025 2.05025C0.737498 3.36301 0 5.14348 0 7C0 12.25 7 20 7 20C7 20 14 12.25 14 7C14 5.14348 13.2625 3.36301 11.9497 2.05025C10.637 0.737498 8.85652 0 7 0Z"
            fill="#19AA1E"
          />
        </svg>

        <p
          className={classNames(
            'text',
            't-body-big',
            styles.address,
            'address',
            'record-card__address',
          )}
        >
          {preparedAddress}
        </p>
      </div>

      <div
        className={classNames(
          'text',
          't-body',
          'text_color_primary',
          styles.description,
          styles.descriptionDesktop,
          'description',
          'record-card__description',
          'record-card__description--desktop',
        )}
      >
        {preparedDescription}
      </div>

      <div
        className={classNames(
          'text',
          't-body',
          'text_color_primary',
          styles.description,
          styles.descriptionMobile,
          'description',
          'record-card__description',
          'record-card__description--mobile mob-true',
        )}
      >
        {preparedMobileDescription}
      </div>

      <div className={classNames(styles.panel, 'panel', 'record-card__panel')}>
        <div
          className={classNames(
            styles.reactions,
            'reactions',
            'record-card__reactions',
          )}
        >
          {reactions.map((reaction, reactionIndex) => {
            const isActive = reactionIndex === currReact

            return (
              <button
                key={reactionIndex}
                type="button"
                className={classNames(
                  styles.reactions_button,
                  'reactions_button',
                  'record-card__reaction-button',
                  {
                    [styles.reactions_button__active]: isActive,
                    reactions_button__active: isActive,
                    'record-card__reaction-button--active': isActive,
                  },
                )}
                aria-pressed={isActive}
                onClick={() => {
                  setCurrReact(previous =>
                    previous === reactionIndex ? null : reactionIndex,
                  )
                }}
              >
                <img
                  className="reactions_button__icon record-card__reaction-icon"
                  src={reaction}
                  alt=""
                />
              </button>
            )
          })}
        </div>

        <div className={classNames(styles.copy, 'copy', 'record-card__copy')}>
          <CopyToClipboard text={`${location.origin}/#section${index}`}>
            <button
              type="button"
              className={classNames(
                styles.copy_button,
                'copy_button',
                'record-card__copy-button',
                {
                  [styles.copy_button__copy]: copy,
                  copy_button__copy: copy,
                  'record-card__copy-button--copied': copy,
                },
              )}
              onClick={() => setCopy(true)}
            >
              {copy ? (
                <svg
                  className="copy_button__icon record-card__copy-icon"
                  width="25"
                  height="25"
                  viewBox="0 0 25 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M9.6553 18.4455C9.6543 18.4455 9.6533 18.4455 9.6513 18.4455C9.3843 18.4445 9.13029 18.3376 8.94329 18.1476L4.9433 14.0855C4.5553 13.6915 4.5603 13.0585 4.9543 12.6715C5.3483 12.2845 5.98029 12.2885 6.36829 12.6825L9.66129 16.0265L18.9493 6.73954C19.3403 6.34854 19.9723 6.34854 20.3633 6.73954C20.7543 7.12954 20.7543 7.76354 20.3633 8.15354L10.3633 18.1535C10.1753 18.3405 9.9203 18.4455 9.6553 18.4455Z"
                    fill="#19AA1E"
                  />
                </svg>
              ) : (
                <svg
                  className="copy_button__icon record-card__copy-icon"
                  width="25"
                  height="25"
                  viewBox="0 0 25 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M21.6553 10.0703V18.8203C21.6553 20.5703 20.7803 21.4453 19.0303 21.4453H10.2803C8.53027 21.4453 7.65527 20.5703 7.65527 18.8203V10.0703C7.65527 8.32031 8.53027 7.44531 10.2803 7.44531H19.0303C20.7803 7.44531 21.6553 8.32031 21.6553 10.0703ZM16.4053 3.44531C16.4053 3.03131 16.0693 2.69531 15.6553 2.69531H6.28027C4.10327 2.69531 2.90527 3.89331 2.90527 6.07031V15.4453C2.90527 15.8593 3.24127 16.1953 3.65527 16.1953C4.06927 16.1953 4.40527 15.8593 4.40527 15.4453V6.07031C4.40527 4.73831 4.94827 4.19531 6.28027 4.19531H15.6553C16.0693 4.19531 16.4053 3.85931 16.4053 3.44531Z"
                    fill="#1A1A1A"
                    fillOpacity="0.4"
                  />
                </svg>
              )}

              <span className="copy_button__text record-card__copy-text">
                {copy
                  ? 'Ссылка на рекорд скопирована'
                  : 'Скопировать ссылку на рекорд'}
              </span>
            </button>
          </CopyToClipboard>
        </div>
      </div>
    </article>
  )
}

export default RecordCard
